/**
 * @file element-factory.ts
 * @description Factory function for creating template elements with defaults
 * 
 * Design Decisions:
 * - Prioritizes JSON-based definitions from ElementDefinitionRegistry
 * - Generates unique IDs using uuid v4
 * - Falls back to hardcoded defaults if JSON definition missing
 * - Special handling for container elements (inherit parent dimensions)
 * - All elements start with visible: true
 * 
 * Creation Flow:
 * 1. Generate unique ID
 * 2. Query ElementDefinitionRegistry for type
 * 3. Merge base properties with JSON defaults
 * 4. Apply type-specific logic (e.g., container sizing)
 * 5. Return fully initialized element
 * 
 * Usage Pattern:
 * - Template designer drag-and-drop
 * - Element duplication
 * - Programmatic element creation
 * 
 * Related:
 * - @see ../element-definitions/element-registry.ts - Definition registry
 * - @see ../types/template.types.ts - Element type definitions
 * - @see ../../ARCHITECTURE.md#adr-001-json-based-element-definitions
 * 
 * @example
 * const textElement = createNewElement('text');
 * // { id: 'uuid', type: 'text', visible: true, fontSize: 12, ... }
 * 
 * const container = createNewElement('container', 800, 600);
 * // { id: 'uuid', type: 'container', width: 800, height: 600, ... }
 */

import { v4 as uuidv4 } from 'uuid';
import type { TemplateElement, ContainerElement } from '../types/template.types';
import { elementRegistry } from '../element-definitions/element-registry';

/**
 * Creates a new template element based on type
 * Uses defaults from element definitions (JSON files)
 */
export function createNewElement(type: string, parentWidth = 680, parentHeight = 400): TemplateElement {
  const baseElement = {
    id: uuidv4(),
    visible: true,
  };

  // Get element definition from registry
  const definition = elementRegistry.getDefinition(type);

  if (definition?.defaults) {
    // Use defaults from JSON definition
    const element = {
      ...baseElement,
      type,
      ...definition.defaults,
    };

    // Special handling for container: use parent dimensions if available
    if (type === 'container') {
      return {
        ...element,
        width: parentWidth,
        height: parentHeight,
      } as ContainerElement;
    }

    return element as TemplateElement;
  }

  // Fallback: hardcoded defaults (should not be reached if all elements have JSON definitions)
  console.warn(`No definition found for element type: ${type}, using fallback defaults`);

  switch (type) {
    case 'container':
      return {
        ...baseElement,
        type: 'container',
        rows: 2,
        columns: 2,
        gap: 1,
        children: [],
        width: parentWidth,  // Volle Parent-Breite
        height: parentHeight, // Volle Parent-Höhe
        horizontalAlign: 'left',
        verticalAlign: 'top',
      } as ContainerElement;

    case 'text':
      return {
        ...baseElement,
        type: 'text',
        content: 'Neuer Text',
        fontSize: 10,
      };

    case 'image':
      return {
        ...baseElement,
        type: 'image',
        width: 200,
        height: 100,
      };

    case 'table':
      return {
        ...baseElement,
        type: 'table',
        tableColumns: ['Pos.', 'Datum', 'Beschreibung', 'Menge', 'Einheit', 'Einzelpreis', 'Betrag'],
      };

    case 'qrcode':
      return {
        ...baseElement,
        type: 'qrcode',
        width: 150,
        height: 150,
      };

    case 'sender':
      return {
        ...baseElement,
        type: 'sender',
        fontSize: 8,
      };

    case 'recipient':
      return {
        ...baseElement,
        type: 'recipient',
        fontSize: 10,
      };

    case 'header':
      return {
        ...baseElement,
        type: 'header',
        content: 'Kopfzeile',
        fontSize: 10,
      };

    case 'page-number':
      return {
        ...baseElement,
        type: 'page-number',
        pageNumberFormat: 'page-x',
        fontSize: 10,
      };

    case 'small-business-note':
      return {
        ...baseElement,
        type: 'small-business-note',
        content: 'Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.',
        fontSize: 9,
      };

    case 'invoice-number':
      return {
        ...baseElement,
        type: 'invoice-number',
        fontSize: 10,
      };

    case 'invoice-date':
      return {
        ...baseElement,
        type: 'invoice-date',
        fontSize: 10,
      };

    case 'payment-due-date':
      return {
        ...baseElement,
        type: 'payment-due-date',
        paymentDueDateFormat: 'date',
        fontSize: 10,
      };

    case 'order-number':
      return {
        ...baseElement,
        type: 'order-number',
        fontSize: 10,
      };

    case 'leitweg-id':
      return {
        ...baseElement,
        type: 'leitweg-id',
        fontSize: 10,
      };

    case 'message':
      return {
        ...baseElement,
        type: 'message',
        content: 'Nachricht an Kunden',
        fontSize: 10,
      };

    case 'iban':
      return {
        ...baseElement,
        type: 'iban',
        fontSize: 10,
      };

    case 'swift':
      return {
        ...baseElement,
        type: 'swift',
        fontSize: 10,
      };

    case 'bank-name':
      return {
        ...baseElement,
        type: 'bank-name',
        fontSize: 10,
      };

    case 'email':
      return {
        ...baseElement,
        type: 'email',
        fontSize: 10,
      };

    case 'website':
      return {
        ...baseElement,
        type: 'website',
        fontSize: 10,
      };

    case 'court':
      return {
        ...baseElement,
        type: 'court',
        fontSize: 10,
      };

    case 'hrn':
      return {
        ...baseElement,
        type: 'hrn',
        fontSize: 10,
      };

    case 'country':
      return {
        ...baseElement,
        type: 'country',
        fontSize: 10,
      };

    case 'tax-number':
      return {
        ...baseElement,
        type: 'tax-number',
        fontSize: 10,
      };

    case 'vat-id':
      return {
        ...baseElement,
        type: 'vat-id',
        fontSize: 10,
      };

    default:
      return {
        ...baseElement,
        type: 'text',
        content: 'Unbekanntes Element',
      };
  }
}
